import * as THREE from "three";
import { isDuctMesh } from "./DuctMaterialGuards.js";

/**
 * MouthMaterialManager
 * Comprehensive material control system for mouth meshes
 * Provides metallicness, roughness, color, and other surface property controls
 */
export class MouthMaterialManager {
    constructor() {
        this.mouthMeshes = new Map(); // Store mouth-related meshes
        this.originalMaterials = new Map(); // Store original materials for restoration
        this.isInitialized = false;

        // Configuration for mouth mesh detection
        this.config = {
            // Mesh names that should be considered mouth meshes
            mouthMeshNames: ["head", "Head"],

            // Material presets for different mouth appearances
            presets: {
                default: {
                    metallicness: 0.1,
                    roughness: 0.8,
                    color: 0xc5c5c5, // Gray mouth color c5c5c5
                    envMapIntensity: 0.3,
                },
                realistic: {
                    metallicness: 0.0,
                    roughness: 0.7,
                    color: 0xcc5555, // Natural mouth color
                    envMapIntensity: 0.3,
                },
                glossy: {
                    metallicness: 0.15,
                    roughness: 0.2,
                    color: 0xdd4444, // Slightly more vibrant
                    envMapIntensity: 0.8,
                },
                matte: {
                    metallicness: 0.0,
                    roughness: 0.9,
                    color: 0xbb4444, // Darker, matte
                    envMapIntensity: 0.1,
                },
                wet: {
                    metallicness: 0.2,
                    roughness: 0.1,
                    color: 0xcc5555, // Natural with high shine
                    envMapIntensity: 1.0,
                    clearcoat: 0.8,
                    clearcoatRoughness: 0.05,
                },
            },
        };

        console.log("MouthMaterialManager initialized");
    }

    /**
     * Initialize the mouth material system by finding relevant meshes
     */
    initialize(model) {
        if (!model) {
            console.warn("No model provided to MouthMaterialManager");
            return false;
        }

        console.log("Initializing mouth material control system...");
        this.mouthMeshes.clear();
        this.originalMaterials.clear();

        let foundMeshes = 0;

        // Find all meshes that might be mouth-related
        model.traverse((child) => {
            if (child.isMesh) {
                const meshName = child.name.toLowerCase();

                // Check if this is a mouth-related mesh
                // The broad "head" match is for the real facial head mesh.
                // Do not let it recolor the eye/teeth duct meshes, whose
                // GLB-authored colors are semantically distinct.
                const isMouthMesh =
                    !isDuctMesh(child) &&
                    this.config.mouthMeshNames.some((name) =>
                        meshName.includes(name.toLowerCase()),
                    );

                if (isMouthMesh) {
                    console.log("Found mouth mesh:", child.name);
                    this.setupMouthMesh(child);
                    foundMeshes++;
                }
            }
        });

        this.isInitialized = foundMeshes > 0;

        if (foundMeshes > 0) {
            console.log(
                `MouthMaterialManager: Found and configured ${foundMeshes} mouth meshes`,
            );

            // Apply default material configuration automatically
            this.applyPreset("default");
            console.log("Applied default mouth material configuration");
        } else {
            console.log("MouthMaterialManager: No mouth meshes found in model");
        }

        return this.isInitialized;
    }

    /**
     * Setup material controls for a mouth mesh
     */
    setupMouthMesh(mesh) {
        if (!mesh.material) {
            console.warn("Mesh has no material:", mesh.name);
            return;
        }

        // Store original material properties
        if (Array.isArray(mesh.material)) {
            // Handle multi-material meshes
            const originalMaterials = mesh.material.map((mat) => ({
                metallicness: mat.metallicness || 0.0,
                roughness: mat.roughness || 0.5,
                color: mat.color ? mat.color.getHex() : 0xffffff,
                envMapIntensity: mat.envMapIntensity || 1.0,
                clearcoat: mat.clearcoat || 0.0,
                clearcoatRoughness: mat.clearcoatRoughness || 0.0,
            }));
            this.originalMaterials.set(mesh.uuid, originalMaterials);
        } else {
            // Handle single material meshes
            const originalMaterial = {
                metallicness: mesh.material.metallicness || 0.0,
                roughness: mesh.material.roughness || 0.5,
                color: mesh.material.color
                    ? mesh.material.color.getHex()
                    : 0xffffff,
                envMapIntensity: mesh.material.envMapIntensity || 1.0,
                clearcoat: mesh.material.clearcoat || 0.0,
                clearcoatRoughness: mesh.material.clearcoatRoughness || 0.0,
            };
            this.originalMaterials.set(mesh.uuid, originalMaterial);
        }

        this.mouthMeshes.set(mesh.uuid, mesh);
        console.log(`Configured mouth material controls for: ${mesh.name}`);
    }

    /**
     * Set metallicness for all mouth meshes
     */
    setMetallicness(value) {
        if (!this.isInitialized) {
            console.warn("MouthMaterialManager not initialized");
            return false;
        }

        const clampedValue = Math.max(0.0, Math.min(1.0, value));

        this.mouthMeshes.forEach((mesh) => {
            this.applyMaterialProperty(mesh, "metallicness", clampedValue);
        });

        console.log(`Set mouth metallicness to: ${clampedValue}`);
        return true;
    }

    /**
     * Set roughness for all mouth meshes
     */
    setRoughness(value) {
        if (!this.isInitialized) {
            console.warn("MouthMaterialManager not initialized");
            return false;
        }

        const clampedValue = Math.max(0.0, Math.min(1.0, value));

        this.mouthMeshes.forEach((mesh) => {
            this.applyMaterialProperty(mesh, "roughness", clampedValue);
        });

        console.log(`Set mouth roughness to: ${clampedValue}`);
        return true;
    }

    /**
     * Set color for all mouth meshes
     */
    setColor(color) {
        if (!this.isInitialized) {
            console.warn("MouthMaterialManager not initialized");
            return false;
        }

        const colorObj = new THREE.Color(color);

        this.mouthMeshes.forEach((mesh) => {
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach((mat) => {
                    if (mat.color) mat.color.copy(colorObj);
                });
            } else {
                if (mesh.material.color) mesh.material.color.copy(colorObj);
            }
        });

        console.log(`Set mouth color to: ${colorObj.getHexString()}`);
        return true;
    }

    /**
     * Set environment map intensity for all mouth meshes
     */
    setEnvMapIntensity(value) {
        if (!this.isInitialized) {
            console.warn("MouthMaterialManager not initialized");
            return false;
        }

        const clampedValue = Math.max(0.0, Math.min(2.0, value));

        this.mouthMeshes.forEach((mesh) => {
            this.applyMaterialProperty(mesh, "envMapIntensity", clampedValue);
        });

        console.log(`Set mouth environment map intensity to: ${clampedValue}`);
        return true;
    }

    /**
     * Set clearcoat properties for all mouth meshes
     */
    setClearcoat(clearcoat, clearcoatRoughness = 0.1) {
        if (!this.isInitialized) {
            console.warn("MouthMaterialManager not initialized");
            return false;
        }

        const clampedClearcoat = Math.max(0.0, Math.min(1.0, clearcoat));
        const clampedRoughness = Math.max(
            0.0,
            Math.min(1.0, clearcoatRoughness),
        );

        this.mouthMeshes.forEach((mesh) => {
            this.applyMaterialProperty(mesh, "clearcoat", clampedClearcoat);
            this.applyMaterialProperty(
                mesh,
                "clearcoatRoughness",
                clampedRoughness,
            );
        });

        console.log(
            `Set mouth clearcoat: ${clampedClearcoat}, roughness: ${clampedRoughness}`,
        );
        return true;
    }

    /**
     * Apply a material preset
     */
    applyPreset(presetName) {
        if (!this.isInitialized) {
            console.warn("MouthMaterialManager not initialized");
            return false;
        }

        const preset = this.config.presets[presetName];
        if (!preset) {
            console.warn(`Unknown mouth material preset: ${presetName}`);
            console.log(
                `Available presets: ${Object.keys(this.config.presets).join(", ")}`,
            );
            return false;
        }

        // Apply all preset properties
        if (preset.metallicness !== undefined)
            this.setMetallicness(preset.metallicness);
        if (preset.roughness !== undefined) this.setRoughness(preset.roughness);
        if (preset.color !== undefined) this.setColor(preset.color);
        if (preset.envMapIntensity !== undefined)
            this.setEnvMapIntensity(preset.envMapIntensity);
        if (preset.clearcoat !== undefined) {
            this.setClearcoat(
                preset.clearcoat,
                preset.clearcoatRoughness || 0.1,
            );
        }

        console.log(`Applied mouth material preset: ${presetName}`);
        return true;
    }

    /**
     * Reset mouth materials to their original state
     */
    resetMaterials() {
        if (!this.isInitialized) {
            console.warn("MouthMaterialManager not initialized");
            return false;
        }

        this.mouthMeshes.forEach((mesh, uuid) => {
            const originalProps = this.originalMaterials.get(uuid);
            if (!originalProps) return;

            if (Array.isArray(mesh.material)) {
                mesh.material.forEach((mat, index) => {
                    if (originalProps[index]) {
                        this.restoreMaterialProperties(
                            mat,
                            originalProps[index],
                        );
                    }
                });
            } else {
                this.restoreMaterialProperties(mesh.material, originalProps);
            }
        });

        console.log("Reset all mouth materials to original state");
        return true;
    }

    /**
     * Get current status and configuration
     */
    getStatus() {
        const status = {
            initialized: this.isInitialized,
            meshCount: this.mouthMeshes.size,
            availablePresets: Object.keys(this.config.presets),
            detectedMeshes: [],
        };

        this.mouthMeshes.forEach((mesh) => {
            status.detectedMeshes.push({
                name: mesh.name,
                materialType: Array.isArray(mesh.material)
                    ? "multi-material"
                    : "single-material",
                materialCount: Array.isArray(mesh.material)
                    ? mesh.material.length
                    : 1,
            });
        });

        return status;
    }

    /**
     * Apply a material property to all materials of a mesh
     */
    applyMaterialProperty(mesh, property, value) {
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach((mat) => {
                if (mat.hasOwnProperty(property)) {
                    mat[property] = value;
                }
            });
        } else {
            if (mesh.material.hasOwnProperty(property)) {
                mesh.material[property] = value;
            }
        }
    }

    /**
     * Restore original material properties
     */
    restoreMaterialProperties(material, originalProps) {
        Object.keys(originalProps).forEach((prop) => {
            if (prop === "color") {
                if (material.color) material.color.setHex(originalProps.color);
            } else if (material.hasOwnProperty(prop)) {
                material[prop] = originalProps[prop];
            }
        });
    }

    /**
     * Dispose of the mouth material manager
     */
    dispose() {
        if (this.isInitialized) {
            this.resetMaterials();
        }

        this.mouthMeshes.clear();
        this.originalMaterials.clear();
        this.isInitialized = false;

        console.log("MouthMaterialManager disposed");
    }
}
